import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import ClientMap from '../components/ClientMap';
import ClientList from '../components/ClientList';
import MessageBoard from '../components/MessageBoard';
import TeamAnalytics from '../components/TeamAnalytics';
import ConversionAnalytics from '../components/ConversionAnalytics';
import LeadsImport from '../components/LeadsImport';
import MapAdmin from '../components/MapAdmin';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import SubNav from '../components/ui/SubNav';
import { colors } from '../theme/tokens';

const VALID_CT_TABS = ['map', 'list', 'analytics', 'import', 'admin'];

const ClientTracking = () => {
  const { token, isSalesTeam, user } = useAuth();
  const navigate = useNavigate();
  const { tab: tabParam } = useParams();
  const showToast = useToast();
  const confirm = useConfirm();

  // Active tab lives in the URL (/client-tracking/:tab) so reloading keeps
  // the user on the same view. We fall back to "map" for any unknown value.
  const activeTab = VALID_CT_TABS.includes(tabParam) ? tabParam : 'map';
  const setActiveTab = (next) => navigate(`/client-tracking/${next}`);

  const [markers, setMarkers] = useState([]);
  const [salesTeam, setSalesTeam] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [leadActions, setLeadActions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMessageBoard, setShowMessageBoard] = useState(false);
  // Analytics tab sub-view: 'team' (performance) | 'conversion' (conversions).
  const [analyticsView, setAnalyticsView] = useState('team');
  // Relocate mode: when set, the map listens for a click to move this client
  const [relocatingClient, setRelocatingClient] = useState(null);
  // Jump-to-marker: switching to map and panning to coordinates
  const [jumpTarget, setJumpTarget] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const isSalesManager = user?.salesRole === 'manager';
  const isRubber = user?.tier === 'rubber';

  // Stale leads: assigned to current user, status=lead, no activity for 30+ days
  const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
  const staleLeads = markers.filter(m =>
    m.claimedBy?.toString() === user?.id?.toString() &&
    m.status === 'lead' &&
    new Date() - new Date(m.lastActionDate) > THIRTY_DAYS
  );

  // Pending filter to push into ClientList when banner is clicked
  const [pendingListFilter, setPendingListFilter] = useState(null);

  const handleStaleBanner = () => {
    setActiveTab('list');
    setPendingListFilter({
      rep: user?.id?.toString(),
      status: 'lead',
      sortBy: 'lastAction',
      sortDir: 'asc',
      ts: Date.now()
    });
  };

  useEffect(() => {
    if (!isSalesTeam) {
      navigate('/');
      return;
    }
    fetchMarkers(true);
    fetchSalesTeam();
    fetchIndustries();
    fetchRegions();
    fetchLeadActions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalesTeam, navigate]);

  // isInitial=true only for the first load — subsequent background refreshes
  // must NOT set loading=true so the map stays mounted and keeps its zoom position.
  const fetchMarkers = async (isInitial = false) => {
    try {
      if (isInitial) setLoading(true);
      const response = await axios.get(`${API_URL}/api/client-markers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMarkers(response.data);
    } catch (err) {
      console.error('Error fetching markers:', err);
      showToast('Failed to load client markers', 'error');
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/regions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegions(response.data);
    } catch (err) {
      console.error('Error fetching regions:', err);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/industries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndustries(response.data);
    } catch (err) {
      console.error('Error fetching industries:', err);
    }
  };

  const fetchSalesTeam = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-markers/sales-team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesTeam(response.data);
    } catch (err) {
      console.error('Error fetching sales team:', err);
    }
  };

  const fetchLeadActions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client-markers/lead-actions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadActions(response.data);
    } catch (err) {
      console.error('Error fetching lead actions:', err);
    }
  };

  const refreshAndSync = async (markerId) => {
    const response = await axios.get(`${API_URL}/api/client-markers`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setMarkers(response.data);
    if (markerId) {
      const updated = response.data.find(m => m._id === markerId);
      if (updated) setSelectedClient(updated);
    }
    return response.data;
  };

  const handleMarkerClick = (marker) => {
    setSelectedClient(marker);
    setShowMessageBoard(true);
  };

  const handleAddMarker = async (clientName, lat, lng) => {
    try {
      await axios.post(
        `${API_URL}/api/client-markers`,
        { clientName, location: { lat, lng }, status: 'lead' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Client marker added successfully!', 'success');
      // Background refresh — no loading state, map stays mounted and keeps zoom
      fetchMarkers();
    } catch (err) {
      console.error('Error adding marker:', err);
      showToast('Failed to add marker: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleStatusChange = async (markerId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      console.error('Error updating status:', err);
      showToast('Failed to update status', 'error');
    }
  };

  const handleAddMessage = async (markerId, messageText) => {
    try {
      await axios.post(
        `${API_URL}/api/client-markers/${markerId}/messages`,
        { text: messageText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      console.error('Error adding message:', err);
      showToast('Failed to add message', 'error');
    }
  };

  const handleQuickAction = async (markerId, action) => {
    try {
      await axios.post(
        `${API_URL}/api/client-markers/${markerId}/quick-action`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Action logged', 'success');
      await refreshAndSync(markerId);
    } catch (err) {
      showToast('Failed to log action: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleArchiveClient = async (markerId) => {
    const ok = await confirm('Are you sure you want to archive this client?', {
      title: 'Archive Client',
      icon: '📦',
      confirmText: 'Archive',
      danger: true
    });
    if (!ok) return;

    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Client archived successfully', 'success');
      setShowMessageBoard(false);
      setSelectedClient(null);
      fetchMarkers();
    } catch (err) {
      console.error('Error archiving client:', err);
      showToast('Failed to archive client: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleClaimClient = async (markerId) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/claim`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      showToast('Failed to claim lead: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleReassignClient = async (markerId, userId) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/reassign`,
        { userId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      showToast('Failed to reassign client: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleUpdateErpCode = async (markerId, erpCode) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/erp-code`,
        { erpCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      showToast('Failed to update customer code: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleSetRegion = async (markerId, regionId) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/region`,
        { regionId: regionId || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      showToast('Failed to update region: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleSetIndustry = async (markerId, industryName) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/industry`,
        { industry: industryName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      showToast('Failed to update industry: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleSaveIndustry = async (industryData) => {
    try {
      if (industryData._id) {
        const response = await axios.put(
          `${API_URL}/api/industries/${industryData._id}`,
          industryData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIndustries(prev => prev.map(i => i._id === industryData._id ? response.data : i));
      } else {
        const response = await axios.post(
          `${API_URL}/api/industries`,
          industryData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setIndustries(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      }
    } catch (err) {
      showToast('Failed to save industry: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleDeleteIndustry = async (industryId) => {
    try {
      await axios.delete(
        `${API_URL}/api/industries/${industryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIndustries(prev => prev.filter(i => i._id !== industryId));
    } catch (err) {
      showToast('Failed to delete industry: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  const handleRenameClient = async (markerId, newName) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/rename`,
        { clientName: newName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshAndSync(markerId);
    } catch (err) {
      showToast('Failed to rename client: ' + (err.response?.data?.error || err.message), 'error');
    }
  };

  // Enter relocate mode: close the message board, switch to map, wait for map click
  const handleStartRelocate = (client) => {
    setRelocatingClient(client);
    setShowMessageBoard(false);
    setActiveTab('map');
  };

  // Called when user clicks a new map position while in relocate mode
  const handleRelocateMarker = async (lat, lng) => {
    if (!relocatingClient) return;
    const clientId = relocatingClient._id;
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${clientId}/location`,
        { lat, lng },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast('Location updated!', 'success');
      const data = await refreshAndSync(clientId);
      const updated = data.find(m => m._id === clientId);
      if (updated) {
        setSelectedClient(updated);
        setShowMessageBoard(true);
      }
    } catch (err) {
      showToast('Failed to update location: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setRelocatingClient(null);
    }
  };

  const handleJumpToMarker = (client) => {
    setActiveTab('map');
    setJumpTarget({ lat: client.location.lat, lng: client.location.lng, ts: Date.now() });
  };

  // Cancel relocate mode and reopen the message board
  const handleCancelRelocate = () => {
    if (relocatingClient) {
      setSelectedClient(relocatingClient);
      setShowMessageBoard(true);
    }
    setRelocatingClient(null);
  };

  const handleMarkMessagesRead = async (markerId) => {
    try {
      await axios.patch(
        `${API_URL}/api/client-markers/${markerId}/messages/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMarkers(prev => prev.map(m => m._id === markerId ? { ...m, unreadCount: 0 } : m));
    } catch (err) {
      console.error('Error marking messages as read:', err);
    }
  };

  const closeMessageBoard = () => {
    setShowMessageBoard(false);
    setSelectedClient(null);
    fetchMarkers();
  };

  if (loading) {
    return <div style={styles.loading}>Loading client tracking...</div>;
  }

  const ctTabs = [
    { key: 'map', label: 'Map View', icon: 'map', active: activeTab === 'map', onClick: () => setActiveTab('map') },
    { key: 'list', label: 'List View', icon: 'format_list_bulleted', active: activeTab === 'list', onClick: () => setActiveTab('list') },
    { key: 'analytics', label: 'Analytics', icon: 'bar_chart', active: activeTab === 'analytics', onClick: () => setActiveTab('analytics') },
    ...(isRubber ? [{ key: 'import', label: 'Import', icon: 'upload_file', active: activeTab === 'import', onClick: () => setActiveTab('import') }] : []),
    ...(isRubber ? [{ key: 'admin', label: 'Maps Admin', icon: 'admin_panel_settings', active: activeTab === 'admin', onClick: () => setActiveTab('admin') }] : [])
  ];

  return (
    <div style={styles.container}>
      <SubNav items={ctTabs} />

      {/* Stale-leads warning banner — visible on map and list tabs only */}
      {staleLeads.length > 0 && (activeTab === 'map' || activeTab === 'list') && (
        <div style={styles.staleBanner} onClick={handleStaleBanner} role="button">
          <span style={styles.staleBannerIcon}>⚠️</span>
          <span>
            You have <strong>{staleLeads.length} lead{staleLeads.length !== 1 ? 's' : ''}</strong> with
            no activity for 30+ days — click to review
          </span>
          <span style={styles.staleBannerArrow}>›</span>
        </div>
      )}

      <div style={styles.content}>
        {activeTab === 'map' && (
          <ClientMap
            markers={markers}
            onMarkerClick={handleMarkerClick}
            onAddMarker={handleAddMarker}
            currentUserId={user?.id}
            isSalesManager={isSalesManager}
            salesTeam={salesTeam}
            relocatingMode={!!relocatingClient}
            relocatingClientId={relocatingClient?._id}
            onRelocateMarker={handleRelocateMarker}
            onCancelRelocate={handleCancelRelocate}
            jumpTarget={jumpTarget}
          />
        )}
        {activeTab === 'list' && (
          <ClientList
            clients={markers}
            onClientClick={handleMarkerClick}
            currentUserId={user?.id}
            pendingFilter={pendingListFilter}
          />
        )}
        {activeTab === 'analytics' && (
          <div>
            <div style={styles.analyticsSwitch}>
              <label style={styles.analyticsSwitchLabel}>View</label>
              <select
                value={analyticsView}
                onChange={(e) => setAnalyticsView(e.target.value)}
                style={styles.analyticsSelect}
              >
                <option value="team">Team Performance</option>
                <option value="conversion">Conversion</option>
              </select>
            </div>
            {analyticsView === 'conversion' ? (
              <ConversionAnalytics token={token} />
            ) : (
              <TeamAnalytics markers={markers} salesTeam={salesTeam} />
            )}
          </div>
        )}
        {activeTab === 'import' && isRubber && (
          <LeadsImport />
        )}
        {activeTab === 'admin' && isRubber && (
          <MapAdmin
            regions={regions}
            onRegionsChange={setRegions}
            markers={markers}
            salesTeam={salesTeam}
          />
        )}
      </div>

      {showMessageBoard && selectedClient && (
        <MessageBoard
          client={selectedClient}
          onClose={closeMessageBoard}
          onStatusChange={handleStatusChange}
          onAddMessage={handleAddMessage}
          onQuickAction={handleQuickAction}
          onArchive={handleArchiveClient}
          onMarkRead={handleMarkMessagesRead}
          onClaim={handleClaimClient}
          onReassign={handleReassignClient}
          onRename={handleRenameClient}
          onUpdateErpCode={handleUpdateErpCode}
          onSetIndustry={handleSetIndustry}
          onSaveIndustry={handleSaveIndustry}
          onDeleteIndustry={handleDeleteIndustry}
          onStartRelocate={handleStartRelocate}
          onJumpToMarker={handleJumpToMarker}
          onSetRegion={handleSetRegion}
          currentUser={user}
          salesTeam={salesTeam}
          industries={industries}
          regions={regions}
          leadActions={leadActions}
        />
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '100%',
    minHeight: 'calc(100vh - 80px)',
    padding: '4px 16px 20px',
    background: 'transparent'
  },
  staleBanner: {
    display: 'flex', alignItems: 'center', gap: '10px',
    background: '#e74c3c', color: 'white',
    padding: '11px 20px', marginBottom: '10px',
    borderRadius: '8px', cursor: 'pointer',
    fontSize: '14px', fontWeight: '600',
    boxShadow: '0 2px 8px rgba(231,76,60,0.35)',
    userSelect: 'none'
  },
  staleBannerIcon: { fontSize: '16px', flexShrink: 0 },
  staleBannerArrow: { marginLeft: 'auto', fontSize: '20px', opacity: 0.8 },
  content: {
    background: colors.glass,
    border: `1px solid ${colors.glassBorder}`,
    borderRadius: '14px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
    overflow: 'hidden'
  },
  loading: {
    textAlign: 'center',
    padding: '50px',
    fontSize: '18px',
    color: colors.textMuted
  },
  analyticsSwitch: {
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '14px 20px 0', flexWrap: 'wrap'
  },
  analyticsSwitchLabel: {
    fontSize: '11.5px', fontWeight: '700', color: colors.textMuted,
    textTransform: 'uppercase', letterSpacing: '0.4px'
  },
  analyticsSelect: {
    padding: '8px 12px', borderRadius: '8px', border: `1px solid ${colors.border}`,
    background: colors.inputBg, color: colors.text, fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit'
  }
};

export default ClientTracking;
